import { api, enc } from '../api';
import { clean } from '../format';
import { go } from '../router';
import { useApp, useLoad } from '../state';
import { useFeedback } from '../ui/feedback';
import { Text } from '../ui/fields';
import { Empty, Loading, PageHead, Tabs } from '../ui/bits';

// Vocabularies are rows; columns come from PRAGMA table_info on the server, so a new column needs no code here.
const NAMES = ['channel', 'motion', 'purpose', 'persona', 'pick_list', 'track_type', 'status_code', 'status_transition', 'metric', 'page_type', 'asset_category', 'program', 'brand', 'playbook', 'playbook_stage', 'agent_skill', 'approval_rule'];
const NUMERIC = ['sort', 'uses', 'code', 'from_code', 'to_code', 'is_active', 'is_default', 'is_terminal', 'seq', 'sla_hours', 'enters_status', 'autonomy_level', 'clean_approvals', 'max_runs_per_day', 'max_cost_cents_per_run', 'prompt_version', 'is_enabled', 'sample_pct', 'requires_distinct_reviewer'];
const ID_KEYED = ['playbook_stage', 'approval_rule'];
const numify = (o: Record<string, any>) => { for (const [k, v] of Object.entries(o)) if (typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v) && NUMERIC.includes(k)) o[k] = Number(v); return o; };

export function Vocab({ name }: { name: string }) {
  const { can } = useApp();
  const { openDialog, toast } = useFeedback();
  const { data, error, reload } = useLoad(() => api('GET', `/vocab/${name}`), [name]);
  const keys = name === 'status_transition' ? ['from_code', 'to_code'] : name === 'pick_list' ? ['list', 'value'] : ID_KEYED.includes(name) ? ['id'] : ['code'];
  const label = name.replace(/_/g, ' ');
  const head = <PageHead title="Vocabularies" sub="Rows, not code: add a channel, a persona or a status without a deploy" />;
  const tabs = <Tabs items={NAMES.map((n) => [n, n.replace(/_/g, ' ')] as [string, string])} current={name} onPick={(n) => go(`#/vocab/${n}`)} />;
  if (error) return <>{head}{tabs}<Empty>{error}</Empty></>;
  if (!data) return <>{head}{tabs}<Loading /></>;
  const { rows, columns } = data as { rows: any[]; columns: string[] };
  const form = (r: Record<string, any> = {}) => <>{columns.map((c) => (keys.includes(c) && r[c] !== undefined ? <input key={c} type="hidden" name={c} value={r[c]} /> : <Text key={c} name={c} label={c} value={r[c]} />))}</>;
  const add = () => openDialog({ title: `Add to ${name}`, okLabel: 'Add', body: form(), onSubmit: async (v) => { await api('POST', `/vocab/${name}`, numify(clean(v))); toast('Added'); reload(); } });
  const edit = (r: any) => {
    const kv = keys.map((k) => String(r[k]));
    openDialog({ title: `Edit ${kv.join(' → ')}`, body: form(r), onSubmit: async (v) => { const body = numify(clean(v)); for (const k of keys) delete body[k]; await api('PATCH', `/vocab/${name}/${kv.map(enc).join('/')}`, body); toast('Saved'); reload(); } });
  };
  return (<>
    <PageHead title="Vocabularies" sub="Rows, not code: add a channel, a persona or a status without a deploy" actions={can('admin') && <button onClick={add}>Add {label}</button>} />
    {tabs}
    <div className="table-wrap"><table><thead><tr>{columns.map((c) => <th key={c}>{c}</th>)}{can('admin') && <th />}</tr></thead><tbody>
      {rows.map((r) => (
        <tr key={keys.map((k) => r[k]).join('/')} className={r.is_active === 0 ? 'muted' : ''}>
          {columns.map((c) => <td key={c}>{c === 'code' || c.endsWith('_code') ? <code>{r[c] ?? ''}</code> : String(r[c] ?? '')}</td>)}
          {can('admin') && <td><button className="quiet" onClick={() => edit(r)}>Edit</button></td>}
        </tr>
      ))}
    </tbody></table></div>
  </>);
}
